import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { inr } from "../lib/format";
import { printDom } from "../lib/printDom";
import { PageHeader, Card, StatTile, Btn, Badge, EmptyState } from "../components/kr";
import { toast } from "sonner";
import { DownloadSimple, Printer, FileXls, ArrowClockwise, EnvelopeSimple } from "@phosphor-icons/react";

const TABS = [
  { k: "3b",       label: "GSTR-3B Summary" },
  { k: "b2b",      label: "GSTR-1 · B2B" },
  { k: "b2c",      label: "GSTR-1 · B2C" },
  { k: "hsn",      label: "HSN Summary" },
  { k: "purchase", label: "Purchase Register" },
];

function useBackendUrl() {
  return process.env.REACT_APP_BACKEND_URL;
}

export default function GstReports() {
  const backend = useBackendUrl();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [tab, setTab] = useState("3b");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/gst/report", { params: { year, month } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setBusy(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      // App uses HttpOnly session cookies for auth (see /api/auth/login). We
      // don't store any token in localStorage — `credentials: "include"` ships
      // the cookie so this fetch is authenticated without exposing anything to
      // XSS.
      const url = `${backend}/api/gst/export.xlsx?year=${year}&month=${month}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `GST_${year}_${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("Excel workbook downloaded");
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const emailToCA = async () => {
    // simple mailto link — non-tech CAs typically want the file attached manually
    const subject = encodeURIComponent(`GST filing package · ${data?.business?.name || ""} · ${data?.period?.label || ""}`);
    const body = encodeURIComponent(
      `Hi,\n\nAttached is our monthly GST filing package for ${data?.period?.label}.\n\n` +
      `Business: ${data?.business?.name} · GSTIN ${data?.business?.gstin}\n\n` +
      `Contents:\n • GSTR-3B Summary\n • GSTR-1 B2B (${data?.counts?.b2b || 0} invoices)\n` +
      ` • GSTR-1 B2C (${data?.counts?.b2c || 0} invoices)\n • HSN Summary (${data?.counts?.hsn_lines || 0} lines)\n` +
      ` • Purchase Register (${data?.counts?.purchase_invoices || 0} invoices)\n\n` +
      `Please attach the Excel workbook downloaded from the CRM.\n\nThanks.`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const years = useMemo(() => {
    const y = today.getFullYear();
    return [y, y - 1, y - 2, y - 3];
  }, [today]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  return (
    <div className="p-6 lg:p-10 print:p-0">
      <div className="print:hidden">
        <PageHeader
          label="COMPLIANCE · ADMIN"
          title="GST Reports for CA"
          subtitle="Monthly filing package — export a single Excel file your CA can consume directly."
          action={
            <div className="flex gap-2 flex-wrap">
              <Btn variant="secondary" onClick={load} data-testid="gst-refresh"><ArrowClockwise size={14} weight="bold" /> Refresh</Btn>
              <Btn variant="secondary" onClick={() => printDom('[data-testid="gst-report-content"]', `GST Report ${data?.period?.label || ""}`)} data-testid="gst-print"><Printer size={14} weight="bold" /> Print</Btn>
              <Btn variant="secondary" onClick={emailToCA} data-testid="gst-email"><EnvelopeSimple size={14} weight="bold" /> Email CA</Btn>
              <Btn onClick={download} data-testid="gst-download"><DownloadSimple size={14} weight="bold" /> Download Excel</Btn>
            </div>
          }
        />

        <Card className="!p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Financial Year</div>
              <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                className="border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:border-orange-500" data-testid="gst-year">
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Month</div>
              <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                className="border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:border-orange-500" data-testid="gst-month">
                {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            {data && (
              <div className="ml-auto text-right">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Period</div>
                <div className="font-bold">{data.period.label}</div>
                <div className="text-[10px] font-mono text-zinc-500">
                  {data.counts.sales_invoices} sales · {data.counts.purchase_invoices} purchases · {data.counts.hsn_lines} HSN lines
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {!data ? (
        <div className="p-10 text-zinc-500">{busy ? "Loading GST data…" : "No data"}</div>
      ) : (
        <div data-testid="gst-report-content">
          {/* Business banner (visible in print too) */}
          <div className="mb-4 p-4 border border-zinc-300 bg-zinc-50 print:mb-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">GST Filing Package</div>
            <div className="font-black text-xl">{data.business.name || "—"}</div>
            <div className="text-xs font-mono text-zinc-600">
              GSTIN: {data.business.gstin || "—"} · State: {data.business.state || "—"} · Period: <b>{data.period.label}</b>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-4 flex flex-wrap gap-2 border-b border-zinc-200 print:hidden">
            {TABS.map(t => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`gst-tab-${t.k}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border-b-4 -mb-px transition ${
                  tab === t.k ? "text-orange-700 border-orange-600" : "text-zinc-500 border-transparent hover:text-zinc-800"
                }`}
              >{t.label}</button>
            ))}
          </div>

          {tab === "3b"       && <Gstr3b data={data} />}
          {tab === "b2b"      && <InvoiceTable rows={data.b2b} kind="b2b" />}
          {tab === "b2c"      && <InvoiceTable rows={data.b2c} kind="b2c" />}
          {tab === "hsn"      && <HsnTable rows={data.hsn_summary} />}
          {tab === "purchase" && <InvoiceTable rows={data.purchase_register} kind="purchase" />}
        </div>
      )}
    </div>
  );
}

function Gstr3b({ data }) {
  const g = data.gstr3b;
  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <SummaryCard title="Outward Supplies (Sales)" tone="green" rows={[
          ["Taxable value", g.outward_taxable_supplies],
          ["Output CGST", g.output_cgst],
          ["Output SGST", g.output_sgst],
          ["Output IGST", g.output_igst],
          ["Total Output Tax", g.total_output_tax, true],
        ]} />
        <SummaryCard title="Inward Supplies (Purchases / ITC)" tone="blue" rows={[
          ["Taxable value", g.input_taxable_purchases],
          ["Input CGST", g.input_cgst],
          ["Input SGST", g.input_sgst],
          ["Input IGST", g.input_igst],
          ["Total Input Tax", g.total_input_tax, true],
        ]} />
        <SummaryCard title="Net Tax Payable" tone="orange" rows={[
          ["Net CGST payable", g.net_cgst_payable],
          ["Net SGST payable", g.net_sgst_payable],
          ["Net IGST payable", g.net_igst_payable],
          ["TOTAL PAYABLE", g.total_tax_payable, true, "text-red-700"],
        ]} />
      </div>
      {(g.net_cgst_carry_forward + g.net_sgst_carry_forward + g.net_igst_carry_forward) > 0 && (
        <Card className="!p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">ITC Carry Forward (input tax exceeds output)</div>
          <div className="grid grid-cols-3 gap-3">
            <MiniBox label="CGST" value={g.net_cgst_carry_forward} />
            <MiniBox label="SGST" value={g.net_sgst_carry_forward} />
            <MiniBox label="IGST" value={g.net_igst_carry_forward} />
          </div>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({ title, rows, tone = "zinc" }) {
  const bg = {
    green:  "bg-green-50 border-green-500",
    blue:   "bg-blue-50 border-blue-500",
    orange: "bg-orange-50 border-orange-500",
    zinc:   "bg-zinc-50 border-zinc-300",
  }[tone];
  return (
    <div className={`border-l-4 ${bg} p-4`}>
      <div className="text-xs font-bold uppercase tracking-widest text-zinc-700 mb-3">{title}</div>
      <table className="w-full font-mono text-sm">
        <tbody>
          {rows.map(([label, value, bold, cls], i) => (
            <tr key={i} className={bold ? "border-t border-zinc-300" : ""}>
              <td className={`py-1 pr-3 ${bold ? "font-bold uppercase text-[10px] tracking-widest" : "text-zinc-600 text-xs"}`}>{label}</td>
              <td className={`py-1 text-right ${bold ? `font-black text-lg ${cls || ""}` : "font-bold"}`}>
                {inr(value || 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MiniBox({ label, value }) {
  return (
    <div className="border border-zinc-300 p-3 bg-white">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono font-black text-xl">{inr(value || 0)}</div>
    </div>
  );
}

function InvoiceTable({ rows, kind }) {
  if (!rows || rows.length === 0) {
    return <Card className="!p-6"><EmptyState title={`No ${kind.toUpperCase()} entries`} hint="Nothing recorded for this period yet." /></Card>;
  }
  const isPurchase = kind === "purchase";
  const isB2B = kind === "b2b";
  const totalTaxable = rows.reduce((s, r) => s + r.taxable_value, 0);
  const totalCgst = rows.reduce((s, r) => s + r.cgst, 0);
  const totalSgst = rows.reduce((s, r) => s + r.sgst, 0);
  const totalIgst = rows.reduce((s, r) => s + r.igst, 0);
  const totalGrand = rows.reduce((s, r) => s + r.total, 0);
  return (
    <Card className="!p-0">
      <table className="kr-table">
        <thead>
          <tr>
            <th>Invoice #</th><th>Date</th>
            {(isB2B || isPurchase) && <th>GSTIN</th>}
            <th>{isPurchase ? "Supplier" : "Party"}</th>
            <th>Place of Supply</th>
            <th className="text-right">Taxable</th>
            <th className="text-right">CGST</th>
            <th className="text-right">SGST</th>
            <th className="text-right">IGST</th>
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="font-mono font-bold text-orange-700">{r.invoice_no}</td>
              <td className="font-mono text-xs">{r.invoice_date}</td>
              {(isB2B || isPurchase) && <td className="font-mono text-xs">{isPurchase ? r.supplier_gstin : r.party_gstin}</td>}
              <td>{isPurchase ? r.supplier_name : r.party_name}</td>
              <td className="text-xs">{r.place_of_supply}</td>
              <td className="font-mono text-right">{inr(r.taxable_value)}</td>
              <td className="font-mono text-right">{inr(r.cgst)}</td>
              <td className="font-mono text-right">{inr(r.sgst)}</td>
              <td className="font-mono text-right">{inr(r.igst)}</td>
              <td className="font-mono text-right font-bold">{inr(r.total)}</td>
            </tr>
          ))}
          <tr className="bg-zinc-100 font-bold">
            <td colSpan={(isB2B || isPurchase) ? 5 : 4} className="text-right uppercase text-xs tracking-widest">Total ({rows.length})</td>
            <td className="font-mono text-right">{inr(totalTaxable)}</td>
            <td className="font-mono text-right">{inr(totalCgst)}</td>
            <td className="font-mono text-right">{inr(totalSgst)}</td>
            <td className="font-mono text-right">{inr(totalIgst)}</td>
            <td className="font-mono text-right text-orange-700">{inr(totalGrand)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}

function HsnTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <Card className="!p-6"><EmptyState title="No HSN lines" /></Card>;
  }
  const totals = rows.reduce((acc, r) => {
    acc.qty += r.total_qty; acc.taxable += r.taxable_value;
    acc.cgst += r.cgst; acc.sgst += r.sgst; acc.igst += r.igst; acc.total += r.total_value;
    return acc;
  }, { qty: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 });
  return (
    <Card className="!p-0">
      <table className="kr-table">
        <thead>
          <tr>
            <th>HSN</th><th>Description</th><th>UQC</th><th>GST%</th>
            <th className="text-right">Qty</th><th className="text-right">Taxable</th>
            <th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th>
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="font-mono font-bold">{r.hsn}</td>
              <td className="text-xs">{r.description}</td>
              <td className="font-mono text-xs">{r.uqc}</td>
              <td className="font-mono text-xs">{r.gst_rate}%</td>
              <td className="font-mono text-right">{r.total_qty.toLocaleString('en-IN')}</td>
              <td className="font-mono text-right">{inr(r.taxable_value)}</td>
              <td className="font-mono text-right">{inr(r.cgst)}</td>
              <td className="font-mono text-right">{inr(r.sgst)}</td>
              <td className="font-mono text-right">{inr(r.igst)}</td>
              <td className="font-mono text-right font-bold">{inr(r.total_value)}</td>
            </tr>
          ))}
          <tr className="bg-zinc-100 font-bold">
            <td colSpan={4} className="text-right uppercase text-xs tracking-widest">Total ({rows.length})</td>
            <td className="font-mono text-right">{totals.qty.toLocaleString("en-IN")}</td>
            <td className="font-mono text-right">{inr(totals.taxable)}</td>
            <td className="font-mono text-right">{inr(totals.cgst)}</td>
            <td className="font-mono text-right">{inr(totals.sgst)}</td>
            <td className="font-mono text-right">{inr(totals.igst)}</td>
            <td className="font-mono text-right text-orange-700">{inr(totals.total)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}
